import { useEffect, useRef } from 'react';
import { useAppStore } from '@/store/app-store';
import { xtreamAPI } from '@/services/xtream-api';
import toast from 'react-hot-toast';

/**
 * Preload only Live TV data after authentication.
 * VOD/Series will load on demand to save bandwidth.
 */
export function useDataPreloader() {
  const {
    isAuthenticated,
    liveStreams,
    setLiveCategories,
    setLiveStreams,
    setIsPreloading,
    setPreloadProgress,
  } = useAppStore();

  const hasPreloaded = useRef(false);
  const lastRefreshTime = useRef<number>(0);

  useEffect(() => {
    if (!isAuthenticated || hasPreloaded.current) return;

    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;
    const hasData = liveStreams.length > 0;

    if (hasData && timeSinceLastRefresh < 5 * 60 * 1000) {
      console.log('Live data fresh, skipping preload');
      hasPreloaded.current = true;
      return;
    }

    const preloadData = async () => {
      setIsPreloading(true);
      const progress = { live: false, vod: false, series: false };

      try {
        // Preload Live TV only
        const [liveCategories, liveData] = await Promise.all([
          xtreamAPI.getLiveCategories(),
          xtreamAPI.getLiveStreams(),
        ]);

        if (Array.isArray(liveCategories) && Array.isArray(liveData)) {
          setLiveCategories(liveCategories);
          setLiveStreams(liveData);
          progress.live = true;
          setPreloadProgress({ ...progress });
          console.log(`Preloaded Live TV: ${liveData.length} channels`);
          toast.success(`Loaded ${liveData.length} Live TV channels!`, { duration: 2000 });
        }

        hasPreloaded.current = true;
        lastRefreshTime.current = Date.now();
      } catch (error: any) {
        console.error('Preload error:', error);
      } finally {
        setIsPreloading(false);
      }
    };

    preloadData();
  }, [isAuthenticated, liveStreams.length, setLiveCategories, setLiveStreams, setIsPreloading, setPreloadProgress]);

  return {
    isPreloading: useAppStore((state) => state.isPreloading),
    preloadProgress: useAppStore((state) => state.preloadProgress),
  };
}
